import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import camelCaseRecursive from 'camelcase-keys-recursive';
import snakeCaseKeys from 'snakecase-keys';
import { useAbility } from '@casl/react';

import {
  approveOtherRequestRequest,
  deleteOtherRequestRequest,
  exportOtherRequestRequest,
  indexOtherRequestRequest,
  rejectOtherRequestRequest
} from '../../../requests/otherRequests';
import {
  ComponentDataTable,
  SimpleCenteredModal,
  DefaultModal,
  Reminder,
  Icon,
  ButtonTooltip
} from '../../../components';
import { downloadFile } from '../../../services/utils';
import { sendAlert } from '../../../actions/utils';
import { AbilityContext } from '../../../config/abilityContext';
import OtherRequestForm from './OtherRequestForm';
import OtherRequestRejectForm from './OtherRequestRejectForm';
import ItemsOtherRequest from '../../Employee/Items/ItemsOtherRequest';

const OtherRequestDataTable = ({
  columns,
  customParams,
  defaultEndDate = null,
  defaultStartDate = null,
  employee,
  employeeId,
  handleFailureRequest,
  moreData,
  nameRangePicker,
  preName,
  setMoreData
}) => {
  const ability = useAbility(AbilityContext);
  const [amount, setAmount] = useState(0);
  const [defaultModalBody, setDefaultModalBody] = useState('');
  const [defaultModalShow, setDefaultModalShow] = useState(false);
  const [defaultModalTitle, setDefaultModalTitle] = useState('');
  const [otherRequests, setOtherRequests] = useState([]);
  const [modalAction, setModalAction] = useState(() => null);
  const [modalBody, setModalBody] = useState('');
  const [modalItem, setModalItem] = useState('');
  const [modalShow, setModalShow] = useState(false);
  const [modalSize, setModalSize] = useState('lg');
  const [modalTitle, setModalTitle] = useState('');
  const [onRequest, setOnRequest] = useState(true);
  const [query, setQuery] = useState({});
  const [reminderItem, setReminderItem] = useState({});
  const [reminderShow, setReminderShow] = useState(false);
  const dispatch = useDispatch();

  const handleSuccessIndex = response => {
    const { data, metadata } = response.data;
    setOtherRequests(data);
    setAmount(metadata.amount);
    setOnRequest(false);
  };

  const handleModalClose = () => {
    setModalShow(false);
    setModalTitle('');
    setModalBody('');
    setOnRequest(false);
  };

  const handleIndexRequest = async params => {
    setOnRequest(true);
    const sendParams = { ...customParams, ...params };
    setQuery(sendParams);
    indexOtherRequestRequest({ dispatch, params: sendParams, successCallback: handleSuccessIndex });
  };

  const handleSuccessRequest = status => {
    switch (status) {
      case 'approved':
        dispatch(sendAlert({ kind: 'success', message: 'Solicitud aceptada correctamente' }));
        break;
      case 'rejected':
        dispatch(sendAlert({ kind: 'success', message: 'Solicitud rechazada correctamente' }));
        break;
      default:
        break;
    }
    setMoreData(!moreData);
    setModalShow(false);
  };

  const handleApproveRequest = (otherRequest, setSubmitting) => {
    const { id: otherRequestId } = otherRequest.otherRequest;
    const myParams = snakeCaseKeys(otherRequest);

    approveOtherRequestRequest(otherRequestId, {
      dispatch,
      params: myParams,
      successCallback: () => handleSuccessRequest('approved'),
      failureCallback: error => {
        dispatch(sendAlert({ kind: 'error', message: error.response?.data?.message }));
        setMoreData(!moreData);
        setModalShow(false);
      },
      callback: () => setSubmitting(false)
    });
  };

  const handleRejectRequest = otherRequest => {
    const { id: otherRequestId } = otherRequest.otherRequest;
    const myParams = snakeCaseKeys(otherRequest);

    rejectOtherRequestRequest(otherRequestId, {
      dispatch,
      params: myParams,
      formData: true,
      successCallback: () => handleSuccessRequest('rejected')
    });
  };

  const handleApproveModal = otherRequest => {
    const reviewerSignature = otherRequest.requestReviewerSignature;

    setModalTitle(reviewerSignature ? 'Firmar Documento' : 'Aprobar Solicitud');
    setModalBody(
      <OtherRequestForm
        formRequest={handleApproveRequest}
        handleModalClose={() => handleModalClose()}
        otherRequest={otherRequest}
        reviewerSignature={reviewerSignature}
        employee={employee}
        rrhh
      />
    );
    setModalShow(true);
  };

  const handleRejectModal = otherRequest => {
    setModalBody(
      <OtherRequestRejectForm
        otherRequest={otherRequest}
        formRequest={handleRejectRequest}
        handleModalClose={() => handleModalClose()}
      />
    );
    setModalTitle('Rechazar solicitud');
    setModalShow(true);
  };

  const handleConfirmModal = item => {
    setDefaultModalShow(true);
    setModalItem(item);
    setDefaultModalBody('¿Estás seguro que deseas eliminar esta solicitud?');
    setDefaultModalTitle('Eliminar solicitud');
  };

  const handleDefaultModalClose = () => {
    setDefaultModalShow(false);
  };

  const handleSuccessRemove = () => {
    dispatch(sendAlert({ kind: 'success', message: 'Solicitud eliminada con éxito' }));
    setMoreData(!moreData);
  };

  const handleDeleteRequest = item => {
    setOnRequest(true);
    deleteOtherRequestRequest(item?.id, {
      dispatch,
      successCallback: handleSuccessRemove,
      callback: () => {
        setDefaultModalShow(false);
        setOnRequest(false);
      }
    });
  };

  const handleActions = (item, action) => {
    setModalItem(item);
    switch (action) {
      case 'approved':
        handleApproveModal(camelCaseRecursive(item));
        setModalSize('lg');
        break;
      case 'rejected':
        handleRejectModal(camelCaseRecursive(item));
        setModalSize('md');
        break;
      case 'destroy':
        setModalAction(() => handleDeleteRequest);
        handleConfirmModal(item);
        break;
      case 'show':
        setModalTitle('Solicitud');
        setModalBody(<ItemsOtherRequest type="other_request" item={item} />);
        setModalShow(true);
        setModalSize('lg');
        break;
      case 'resend':
        setReminderShow(true);
        setReminderItem(item);
        break;
      default:
        console.log('Error: Action not found');
        break;
    }
  };

  const sortColumnCase = name => {
    switch (name) {
      case 'employee':
        return { sort_employees: name };
      case 'status':
        return { sort_status: name };
      default:
        return { sort_column: name };
    }
  };

  const handleDownload = () => {
    setOnRequest(true);

    exportOtherRequestRequest({
      dispatch,
      params: query,
      successCallback: response => {
        setModalShow(false);
        downloadFile(response);
        setOnRequest(false);
      },
      failureCallback: handleFailureRequest
    });
  };

  const handleMassAction = action => {
    switch (action) {
      case 'export':
        setDefaultModalTitle('Exportar otras solicitudes');
        setDefaultModalShow(true);
        setDefaultModalBody('¿Estás seguro que deseas descargar otras solicitudes?');
        setModalAction(() => handleDownload);
        break;
      default:
        console.log('Error: Action not found');
    }
  };

  return (
    <>
      <div className='data-table-yellow'>
        <ComponentDataTable
          onRequest={onRequest}
          columns={columns(handleActions)}
          data={otherRequests}
          totalRows={amount}
          moreData={moreData}
          handleSortCase={sortColumnCase}
          resourceRequest={handleIndexRequest}
          key={employeeId}
          withStartDate
          withEndDate
          onRowClicked={item => handleActions(item, 'show')}
          pointerOnHover
          defaultStartDate={defaultStartDate}
          defaultEndDate={defaultEndDate}
          preName={preName || 'other_requests'}
          rangePicker
          nameRangePicker={nameRangePicker}
          withMassActions
          massActions={
            <>
              {ability.can('export', 'OtherRequest') && (
                <ButtonTooltip
                  variant="circle-primary"
                  className="mr-2 btn-circle mb-3"
                  text="Exportar"
                  onClick={() => handleMassAction('export')}
                >
                  <Icon icon="download-2" />
                </ButtonTooltip>
              )}
            </>
          }
        />
        <SimpleCenteredModal
          size={modalSize}
          title={modalTitle}
          body={modalBody}
          show={modalShow}
          onHide={() => setModalShow(false)}
        />
      </div>
      <DefaultModal
        title={defaultModalTitle}
        body={defaultModalBody}
        show={defaultModalShow}
        handleClose={handleDefaultModalClose}
        handleConfirm={() => modalAction(modalItem)}
        disabled={onRequest}
      />
      <Reminder
        item={reminderItem}
        show={reminderShow}
        modalShow={setReminderShow}
        moreData={moreData}
        setOnRequest={setOnRequest}
        setMoreData={setMoreData}
      />
    </>
  );
};

export default OtherRequestDataTable;